import React from 'react';
import PropTypes from 'prop-types';
import { translate } from 'helpers/translateHelper';
import colors from 'theme/_colors.scss';
import { IconAnnualMaintenanceBarChartLegend, IconFault, IconWarningType } from 'svgIcons/MotionPortalIcons';
import { OVER_ALL_STATUS } from './constants';
import { formatDate, formatDateTime } from 'helpers/dateTimeFormatter';

const EventsCustomTooltip = ({ timestamp, events, condition, showTimePortion }) => {
    const formattedDate = showTimePortion
        ? formatDateTime(timestamp)
        : formatDate(timestamp);
    const isGoodOrUnknown = [OVER_ALL_STATUS.Good, OVER_ALL_STATUS.Unknown].includes(OVER_ALL_STATUS[condition]);
    // Sort: Faults first (alphabetically by message), then Warnings (alphabetically by message)
    const sortedEvents = (events || []).slice().sort((a, b) => {
        if (a?.severity === b?.severity) {
            // Alphabetical order by message (case-insensitive)
            return (a?.message || '').localeCompare(b?.message || '', undefined, { sensitivity: 'base' });
        }
        // Faults first
        if (a?.severity === 'Fault') { return -1; }
        if (b?.severity === 'Fault') { return 1; }
        return 0;
    });

    // show only first N items, compute remaining unique count and total occurrences
    const MAX_ITEMS = 10;
    const displayedEvents = sortedEvents?.slice(0, MAX_ITEMS) || [];
    const remainingUniqueCount = sortedEvents?.length - MAX_ITEMS;
    const totalOccurrences = events?.length;

    return (
        <div className='custom-events-tooltip'>
            <div className='t-header'>
                <span className='t-date'>{formattedDate}</span>
            </div>
            <div className='divider' />
            <div className='t-item'>
                {isGoodOrUnknown ?
                    <div className='event-row'>
                        <IconAnnualMaintenanceBarChartLegend
                            width={16}
                            height={16}
                            viewBox={'0 0 16 16'}
                            borderRadius={0}
                            color={OVER_ALL_STATUS[condition] === OVER_ALL_STATUS.Good ? colors.jungleGreen : colors.smoke}
                            className='event-icon'
                        />
                        <span className='event-message'>
                            {OVER_ALL_STATUS[condition] === OVER_ALL_STATUS.Good ?
                                translate('ABB.Powertrain.Frontend.goodEventConditionLabel')
                                : translate('ABB.Powertrain.Frontend.noDataLabelAvailable')
                            }
                        </span>
                    </div>
                    :
                    <>
                        {displayedEvents.map((ev, idx) =>
                            <div key={idx} className='event-row'>
                                <div className='event-icon-container'>
                                    {ev.severity === 'Fault'
                                        ? <IconFault className='event-icon' />
                                        : <IconWarningType className='event-icon' />
                                    }
                                </div>
                                <div className='event-content'>
                                    {ev?.message &&
                                        <div className='event-message'>
                                            {ev?.message}
                                            {ev?.code ? `(${ev?.code})` : ''}
                                        </div>
                                    }
                                    {Number(ev?.count) > 1 && <>
                                        <div className='event-line'>|</div>
                                        <div className='event-count'>×{ev?.count}</div>
                                    </>}
                                </div>
                            </div>
                        )}

                        {remainingUniqueCount > 0 &&
                            <>
                                <div className='event-more-divider' /> {/* border after listed items */}
                                <div className='event-more'>
                                    <span>+{remainingUniqueCount}</span> {translate('ABB.Powertrain.Frontend.uniqueEvents')} ({translate('ABB.Powertrain.Frontend.totalOccurrences')}: <span>{totalOccurrences}</span>)
                                    <div className='event-more-note'>{translate('ABB.Powertrain.Frontend.checkDetailsInEventLog')}</div>
                                </div>
                            </>
                        }
                    </>
                }
            </div>
        </div >
    );
};

EventsCustomTooltip.propTypes = {
    timestamp: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.number,
        PropTypes.instanceOf(Date)
    ]),
    events: PropTypes.arrayOf(PropTypes.shape({
        severity: PropTypes.string,
        code: PropTypes.string,
        message: PropTypes.string,
        count: PropTypes.number
    })),
    condition: PropTypes.string,
    showTimePortion: PropTypes.bool,
};

export default EventsCustomTooltip;
